import { useMutation, useQuery } from '@apollo/client/react'
import { ErrorState, LoadingState, useLanguage, useTranslate } from '@athena/ui'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { AuthScreen } from '../features/auth/AuthScreen.js'
import { OnboardingFlow } from '../features/auth/OnboardingFlow.js'
import {
  CompleteOnboardingDocument,
  SelectOnboardingRoleDocument,
  MeDocument,
} from '../graphql/generated/operations.js'

export function OnboardingScreen({ invitation = false }: { invitation?: boolean }) {
  const t = useTranslate()
  const language = useLanguage()
  const navigate = useNavigate()
  const { data, loading, error: loadError, refetch } = useQuery(MeDocument)
  const [selectRole, { loading: selecting }] = useMutation(SelectOnboardingRoleDocument)
  const [complete, { loading: completing }] = useMutation(CompleteOnboardingDocument)
  const [error, setError] = useState<string>()
  const [showCompletion, setShowCompletion] = useState(false)
  const [completionReady, setCompletionReady] = useState(false)
  const viewer = data?.me
  useEffect(() => {
    if (!showCompletion) return
    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const timer = window.setTimeout(() => setCompletionReady(true), reduceMotion ? 0 : 500)
    return () => window.clearTimeout(timer)
  }, [showCompletion])
  useEffect(() => {
    if (loading || loadError) return
    if (!viewer) void navigate({ to: '/$language/sign-in', params: { language }, replace: true })
    else if (viewer.onboarding.complete && (!showCompletion || completionReady))
      void navigate({
        to: invitation
          ? '/$language/invite'
          : viewer.student && !viewer.tutor
            ? '/$language/learn'
            : '/$language',
        params: { language },
        replace: true,
      })
  }, [viewer, loading, loadError, navigate, language, invitation, showCompletion, completionReady])
  if (loading && !viewer) return <LoadingState>{t('common.loading')}</LoadingState>
  if (loadError) return <ErrorState>{t('appError.unreachableBody')}</ErrorState>
  if (!viewer || (viewer.onboarding.complete && !showCompletion)) return null
  return (
    <AuthScreen
      title={t('onboarding.title')}
      subtitle={t('onboarding.subtitle')}
      backToSite={false}
      progressStage={viewer.onboarding.role ? 3 : 2}
      progressComplete={showCompletion}
    >
      <OnboardingFlow
        state={viewer.onboarding}
        saving={selecting || completing}
        completed={showCompletion}
        error={error}
        selectRole={async (role) => {
          setError(undefined)
          try {
            await selectRole({ variables: { role } })
            await refetch()
          } catch {
            setError(t('onboarding.error'))
          }
        }}
        complete={async (input) => {
          setError(undefined)
          setCompletionReady(false)
          try {
            await complete({ variables: { input } })
            setShowCompletion(true)
            await refetch()
          } catch {
            setShowCompletion(false)
            setCompletionReady(false)
            setError(t('onboarding.error'))
          }
        }}
      />
    </AuthScreen>
  )
}
